/**
 * Every tool's URL slug and every docs page's legacy slug, in one place.
 *
 * Tools are referred to in code by a short stable id ("create-spl") — the
 * key of the FEES map, the ToolFaq map, a doc page's `tool` field, the
 * `fee: { tool }` option on a TxFlow. The URL they live at is the longer,
 * keyword-rich slug on the right ("create-solana-spl-token"), chosen for
 * search: the marketing site, docs, blog and sitemap all link to these, and
 * the docs page for a tool sits at /docs/<the same slug>.
 *
 * Renaming a tool's URL is a one-line change here; every link, route and
 * redirect is derived. Old slugs go in LEGACY_TOOL_SLUGS / LEGACY_DOC_SLUGS
 * so links already out in the wild keep working (client-side redirect in
 * the app, 301 from the Worker for docs).
 *
 * Dependency-free on purpose: worker/index.js imports this too.
 */

export const TOOL_PATHS = {
  "create-spl": "create-solana-spl-token",
  "create-spl22": "create-solana-token-2022",
  launch: "launch-token-on-pumpfun",
  "launch-bonk": "launch-token-on-letsbonk",
  "launch-meteora": "launch-token-on-meteora-dbc",
  stonkfun: "launch-token-on-stonkfun",
  "creator-fees": "claim-solana-creator-fees",
  "add-liquidity": "add-liquidity-solana",
  "remove-liquidity": "remove-liquidity-solana",
  "grid-bot": "solana-grid-trading-bot",
  "liquidity-bot": "meteora-dlmm-liquidity-bot",
  launchpad: "solana-token-launchpad",
  multisender: "solana-multisender",
  airdrop: "solana-token-airdrop",
  manage: "solana-token-manager",
  burn: "burn-solana-tokens",
  "rent-reclaim": "reclaim-solana-rent",
  "wallet-cleaner": "solana-wallet-cleaner",
  "wallet-aggregator": "solana-wallet-aggregator",
  snapshot: "solana-token-holder-snapshot",
  "my-tokens": "my-solana-tokens",
  "send-jobs": "solana-multisend-jobs",
  "token-inspector": "solana-token-inspector",
  "smart-money": "solana-smart-money-tracker",
  "stonkfun-rewards": "stonkfun-rewards-tracker",
  "tx-inspector": "solana-transaction-inspector",
  account: "solana-account-inspector",
  status: "solana-network-status",
  sign: "sign-verify-solana-message",
  vanity: "solana-vanity-address-generator",
  faucet: "solana-devnet-faucet",
  "lp-locker": "solana-lp-token-locker",
  "lp-lock-verifier": "solana-lp-lock-verifier",
  "token-locker": "solana-token-locker",
  "token-lock-verifier": "solana-token-lock-verifier",
};

/** URL slug for a tool id; an unknown value (already a slug, or a nested
 * path like "launchpad/sale/abc") passes through unchanged. */
export const toolPath = (idOrSlug = "") => {
  const [head, ...rest] = String(idOrSlug).replace(/^\//, "").split("/");
  const slug = TOOL_PATHS[head] || head;
  return [slug, ...rest].join("/");
};

const TOOL_ID_BY_SLUG = Object.fromEntries(Object.entries(TOOL_PATHS).map(([id, slug]) => [slug, id]));

/** Tool id for a URL slug ("create-solana-spl-token" → "create-spl"); a
 * legacy slug or an id resolves too, anything else comes back as-is. */
export const toolIdFromSlug = (slug = "") =>
  TOOL_ID_BY_SLUG[slug] || LEGACY_TOOL_SLUGS[slug] || slug;

/** Old app URL segment → tool id. The app router redirects each of these
 * (keeping any sub-path and query string) to the tool's current slug. */
export const LEGACY_TOOL_SLUGS = {
  ...Object.fromEntries(Object.keys(TOOL_PATHS).map((id) => [id, id])),
  // The Token Inspector's older names: the generic /token slug it launched
  // under, and /token-check from the retired Token Safety Check.
  token: "token-inspector",
  "token-check": "token-inspector",
  // The pump.fun-only fees page grew into the cross-platform dashboard.
  "pump-fees": "creator-fees",
};

// A slug's words, sorted, without "solana": "reclaim-solana-rent" and
// "solana-rent-reclaim" both key to "reclaim-rent".
const wordKey = (slug) =>
  slug
    .split("-")
    .filter((w) => w && w !== "solana")
    .sort()
    .join("-");

const TOOL_ID_BY_WORDS = Object.fromEntries(Object.entries(TOOL_PATHS).map(([id, slug]) => [wordKey(slug), id]));

/** The tool a mangled URL segment meant, or null. People type slugs from
 * memory with the words in another order ("solana-rent-reclaim") and paste
 * links with text stuck on the end ("reclaim-solana-rent All 26", a
 * trailing full stop), and every such path used to land on the home page.
 * Keeps what comes before the first space, then matches a current or old
 * slug, or a current slug's words in any order with "solana" optional. */
export const guessToolId = (segment = "") => {
  let s = String(segment);
  try {
    s = decodeURIComponent(s);
  } catch {
    // a stray "%" — match it as typed
  }
  s = s
    .trim()
    .split(/\s/)[0]
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/[^a-z0-9]+$/, "");
  if (!s) return null;
  return TOOL_ID_BY_SLUG[s] || LEGACY_TOOL_SLUGS[s] || TOOL_ID_BY_WORDS[wordKey(s)] || null;
};

/** Old docs slug → current docs slug. Served as a 301 by worker/index.js
 * and as a client-side redirect by DocsApp for in-app navigation. */
export const LEGACY_DOC_SLUGS = {
  "non-custodial": "non-custodial-solana-tools",
  "connect-wallet": "connect-solana-wallet",
  networks: "solana-devnet-vs-mainnet",
  authorities: "solana-token-authorities",
  "token-2022": "solana-token-2022-extensions",
  "transaction-lifecycle": "solana-transaction-lifecycle",
  "wallet-signing-errors": "solana-wallet-signing-errors",
  "lookup-data-errors": "solana-lookup-data-errors",
  "create-spl": TOOL_PATHS["create-spl"],
  "create-spl22": TOOL_PATHS["create-spl22"],
  launchpad: TOOL_PATHS.launchpad,
  launch: TOOL_PATHS.launch,
  "launch-pumpfun": TOOL_PATHS.launch,
  "launch-bonk": TOOL_PATHS["launch-bonk"],
  "launch-meteora": TOOL_PATHS["launch-meteora"],
  "pump-fees": TOOL_PATHS["creator-fees"],
  "creator-fees": TOOL_PATHS["creator-fees"],
  multisender: TOOL_PATHS.multisender,
  airdrop: TOOL_PATHS.airdrop,
  manage: TOOL_PATHS.manage,
  "revoke-authority": "revoke-solana-mint-freeze-authority",
  burn: TOOL_PATHS.burn,
  "rent-reclaim": TOOL_PATHS["rent-reclaim"],
  "wallet-cleaner": TOOL_PATHS["wallet-cleaner"],
  "token-locker": TOOL_PATHS["token-locker"],
  snapshot: TOOL_PATHS.snapshot,
  "token-check": TOOL_PATHS["token-inspector"],
  "tx-inspector": TOOL_PATHS["tx-inspector"],
  account: TOOL_PATHS.account,
  sign: TOOL_PATHS.sign,
  vanity: TOOL_PATHS.vanity,
  status: TOOL_PATHS.status,
  faucet: TOOL_PATHS.faucet,
};
